import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Tag, Link as LinkIcon, Users, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { BTagTable } from '@/components/marketing/BTagTable';
import { BTagFormDialog } from '@/components/marketing/BTagFormDialog';

export const Route = createFileRoute('/_admin/btags')({
  component: BTagsPage,
});

interface BTag {
  id: string;
  code: number;
  name: string;
  description: string | null;
  active: boolean;
  total_registrations: number;
  created_at: string;
}

function BTagsPage() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBTag, setEditingBTag] = useState<BTag | null>(null);

  const { data: btags = [], isLoading, error, refetch } = useQuery<BTag[]>({
    queryKey: ['btags'],
    queryFn: async () => {
      const { data } = await api.get('/admin/marketing/btags');
      const raw = Array.isArray(data?.btags) ? data.btags
        : Array.isArray(data?.items) ? data.items
        : Array.isArray(data) ? data
        : [];
      // gRPC proto-loader entrega camelCase; interface local usa snake_case.
      return raw.map((b: any) => ({
        id: b.id,
        code: Number(b.code ?? 0),
        name: b.name ?? '',
        description: b.description ?? null,
        active: b.active ?? true,
        total_registrations: b.totalRegistrations ?? b.total_registrations ?? 0,
        created_at: b.createdAt ?? b.created_at ?? '',
      }));
    },
    staleTime: 30_000,
  });

  const handleEdit = (btag: BTag) => {
    setEditingBTag(btag);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingBTag(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Tag className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">BTags</h1>
            <p className="text-muted-foreground text-sm">Gerenciar códigos de rastreamento de campanhas</p>
          </div>
        </div>
        <Button onClick={() => setIsFormOpen(true)} className="w-full sm:w-auto min-h-[44px]">
          <Plus className="h-4 w-4 mr-2" />
          Nova BTag
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Tag className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total de BTags</p>
                <p className="text-xl font-bold">{btags.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <LinkIcon className="h-4 w-4 text-green-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">BTags Ativas</p>
                <p className="text-xl font-bold">{btags.filter((b) => b.active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Users className="h-4 w-4 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Cadastros</p>
                <p className="text-xl font-bold">
                  {btags.reduce((acc, b) => acc + b.total_registrations, 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {error && (
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <p role="alert" className="text-destructive font-medium">Erro ao carregar BTags</p>
            <p className="text-sm text-muted-foreground">{(error as Error).message}</p>
            <Button variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </CardContent>
        </Card>
      )}

      <BTagTable data={btags} loading={isLoading} onEdit={handleEdit} />

      <BTagFormDialog open={isFormOpen} onOpenChange={handleFormClose} editingBTag={editingBTag} />
    </div>
  );
}
